import { Badge } from "@components/ui/badge";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@components/ui/sidebar";
import {
  EditIcon,
  MessageSquareIcon,
  MoreHorizontalIcon,
  EyeOffIcon,
  Trash2Icon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { Link } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { Trans } from "@lingui/react/macro";
import { SearchLight } from "@/types/Search";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { cn } from "@/lib/utils";
import { INTERCOM_IDS } from "@/intercom";

export function SearchItem({
  to,
  search,
  orgUuid,
  pathname,
  onDelete,
  onMoveUp,
  onMoveDown,
  isInSubMenu = false,
}: {
  to: string;
  search: SearchLight;
  orgUuid: string;
  pathname: string;
  isInSubMenu?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete: () => void;
}) {
  const ContainerComponent = isInSubMenu ? SidebarMenuSubItem : SidebarMenuItem;
  const ButtonComponent = isInSubMenu
    ? SidebarMenuSubButton
    : SidebarMenuButton;

  const settingsTo = ROUTES.MENTION_SETTINGS_ORGID.replace(
    ":orgUuid",
    orgUuid
  ).replace(":uuid", search.uuid);

  const isActive = pathname === to || pathname === settingsTo;

  return (
    <ContainerComponent>
      <ButtonComponent
        render={<Link to={to} data-testid="nav-alert-item" />}
        className={cn("relative group/button", search.has_unread && "unread")}
        isActive={isActive}
      >
        {!isInSubMenu && (
          <div className="relative shrink-0">
            <MessageSquareIcon className="size-4 text-sidebar-accent-foreground" />

            {search.has_unread && (
              <Badge
                className={cn(
                  "p-0 size-2.5 bg-indigo-500 border-2 rounded-full absolute top-0 right-0 -translate-y-0.5 translate-x-0.5",
                  "border-sidebar group-data-[active=true]/button:border-sidebar-accent"
                )}
              />
            )}
          </div>
        )}
        <span>{search.title}</span>
      </ButtonComponent>

      <AccessControl level={ACCESS_LEVELS.USER}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger
            render={
              <SidebarMenuAction
                showOnHover
                className={
                  isInSubMenu
                    ? "top-1 group-focus-within/menu-sub-item:opacity-100 group-hover/menu-sub-item:opacity-100"
                    : undefined
                }
                data-testid="search-actions-dropdown-trigger"
              />
            }
          >
            <MoreHorizontalIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              render={
                <Link
                  to={settingsTo}
                  data-testid="settings-link"
                  data-intercom={INTERCOM_IDS.MAIN_MENU_ALERT_SETTINGS}
                />
              }
            >
              <EditIcon />
              <Trans>Redigera bevakning</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem
              render={
                <Link
                  to={ROUTES.MENTION_SETTINGS_HIDDEN_MENTIONS_ORGID.replace(
                    ":orgUuid",
                    orgUuid
                  ).replace(":uuid", search.uuid)}
                  data-testid="hidden-mentions-link"
                />
              }
            >
              <EyeOffIcon />
              <Trans>Borttagna omnämnanden</Trans>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} variant="destructive">
              <Trash2Icon />
              <Trans>Ta bort bevakning</Trans>
            </DropdownMenuItem>
            {onMoveUp && (
              <DropdownMenuItem onClick={onMoveUp}>
                <ArrowUpIcon />
                <Trans>Flytta upp</Trans>
              </DropdownMenuItem>
            )}
            {onMoveDown && (
              <DropdownMenuItem onClick={onMoveDown}>
                <ArrowDownIcon />
                <Trans>Flytta ned</Trans>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </AccessControl>
    </ContainerComponent>
  );
}
